import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { useUserListBox } from "@jugl-web/domain-resources/users/hooks/useUserListBox";
import {
  ListBoxItemComponent,
  ListBoxItemComponentProps,
} from "@jugl-web/ui-components";
import {
  ResourcePickerPopover,
  ResourcePickerPopoverProps,
} from "@jugl-web/ui-components/web/ResourcePickerPopover";
import { Avatar } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useState } from "react";

interface TicketAssigneesPickerPopoverProps
  extends Pick<ResourcePickerPopoverProps<string>, "renderTrigger"> {
  assigneeIds: string[];
  /** Callback that is called when the user confirms the selection */
  onSelectionConfirm: (assigneeIds: string[]) => void;
  /** Callback that is called when the user changes the selection */
  onSelectionChange?: (assigneeIds: string[]) => void;
}

export const TicketAssigneesPickerPopover: FC<
  TicketAssigneesPickerPopoverProps
> = ({ assigneeIds, onSelectionConfirm, onSelectionChange, renderTrigger }) => {
  const { entityId } = useEntitySelectedProvider();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { getListBoxProps, clearSearchQuery } = useUserListBox({
    entityId,
    topUsers: assigneeIds,
    skipLoading: !isPopoverOpen,
  });

  const handleSubmit = (newAssigneeIds: string[]) => {
    const currentAssigneeIdsSerialized = assigneeIds.sort().join(",");
    const newAssigneeIdsSerialized = newAssigneeIds.sort().join(",");

    if (currentAssigneeIdsSerialized === newAssigneeIdsSerialized) {
      return;
    }

    onSelectionConfirm(newAssigneeIds);
  };

  return (
    <ResourcePickerPopover
      placement="bottom"
      className="w-[312px]"
      renderTrigger={renderTrigger}
      onOpenStateChange={(isOpen) => {
        if (!isOpen) {
          clearSearchQuery();
        }
        setIsPopoverOpen(isOpen);
      }}
      onSelectionChange={onSelectionChange}
      onSubmit={handleSubmit}
      submitMode="onClose"
      {...getListBoxProps({
        itemSize: "md",
        spaceBetweenItems: "compact",
        defaultSelectedIds: assigneeIds,
        renderCustomItem: (item, _, props) => (
          <UserListBoxItemCompact
            entityId={entityId}
            userId={item.id}
            {...props}
          />
        ),
      })}
    />
  );
};

interface UserListBoxItemCompactProps extends ListBoxItemComponentProps {
  entityId: string;
  userId: string;
}

const UserListBoxItemCompact: FC<UserListBoxItemCompactProps> = ({
  entityId,
  userId,
  ...listBoxItemProps
}) => {
  const { safeProfile } = useUserGeneralProfile({ entityId, userId });

  return (
    <ListBoxItemComponent
      {...listBoxItemProps}
      label={safeProfile.displayName}
      className={cx(listBoxItemProps.isSelected && "font-medium")}
      startSlot={
        <Avatar
          size="sm"
          imageUrl={safeProfile.avatar}
          username={safeProfile.displayName}
          className="border border-solid border-[#F2F2F2]"
        />
      }
    />
  );
};
